import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { tokenRefreshService } from '../utils/tokenRefresh';
import { useNotificationStore } from './notificationStore';
import { executeWhenHydrated } from '../utils/persistUtils';
import { User } from '../types/api';
import { websocketService } from '../api/websocketService';
import { safeStorage } from '../utils/safeStorage';

// User data interface - extends the consolidated User interface with uid field
export interface UserData extends Omit<User, 'id'> {
  uid: string; // Use uid instead of id for consistency with Firebase
}

// Helper function to convert User (from API) to UserData (for store)
export const userToUserData = (user: User): UserData => {
  const { id, ...rest } = user;
  return { ...rest, uid: id };
};

// Helper function to convert UserData (from store) to User (for API)
export const userDataToUser = (userData: UserData): User => {
  const { uid, ...rest } = userData;
  return { ...rest, id: uid };
};

// Store interface
interface UserStore {
  // State
  user: UserData | null;
  isAuthenticated: boolean;
  hasToken: boolean;
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  init: () => void;
  setUser: (user: UserData) => void;
  updateUser: (updates: Partial<UserData>) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;

  // Token management
  getToken: () => string | null;
  setToken: (token: string) => void;
  removeToken: () => void;
  ensureValidToken: () => Promise<boolean>;

  // Computed values
  getFullName: () => string;
  getLocation: () => string;
  getCompletion: () => { completed: number; total: number; percentage: number };
}

// Create the store
export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      // Restore session from Zustand's own persisted state (hydrated automatically).
      // Previously this read from a separate localStorage key ('userData') which
      // could get out of sync with the store, causing phantom logouts.
      init: () => {
        const user = get().user;
        const userToken = get().getToken();

        // Also migrate legacy 'userData' key into the store if Zustand state is empty
        // but the old key exists (one-time migration for users who logged in before
        // the store was the source of truth).
        if (!user && !userToken) {
          const legacyData = safeStorage.getItem('userData');
          const legacyToken = safeStorage.getItem('userToken');
          if (legacyData && legacyToken) {
            try {
              const parsed = JSON.parse(legacyData);
              set({ user: parsed, isAuthenticated: true, hasToken: true, isInitialized: true, isLoading: false });
              // Legacy data migrated — continue with token monitoring below
              get().init();
              return;
            } catch {
              // Corrupt legacy data, ignore
            }
          }
          // No stored session — mark as initialized so route guards don't spin forever
          set({ isInitialized: true, isLoading: false });
          return;
        }

        if (!user || !userToken) {
          // Partial state (user without token or vice versa) — clear and mark initialized
          set({ isInitialized: true, isLoading: false });
          return;
        }

        // We have both user data and a token from Zustand's persisted state.
        if (tokenRefreshService.isTokenValid(userToken)) {
          // Token is still valid — mark authenticated and start monitoring
          set({ user, isAuthenticated: true, hasToken: true, isInitialized: true, isLoading: false });
          // Keep legacy key in sync for any code that still reads it
          safeStorage.setItem('userData', JSON.stringify(user));
          try {
            tokenRefreshService.startTokenMonitoring(
              userToken,
              (newToken) => get().setToken(newToken),
              (error) => {
                // Token refresh failed — do NOT nuke the session.
                // The user stays "authenticated" until their next API call
                // gets a 401, at which point the API interceptor will handle it.
                console.error('Automatic token refresh failed on init:', error);
              },
              () => get().getToken()
            );
          } catch (error) {
            console.error('Error starting token monitoring on init:', error);
          }
        } else {
          // Token has expired — try a silent refresh, but keep the session alive
          // during the attempt so the user doesn't see a flash of the login screen.
          set({ user, isAuthenticated: true, hasToken: true, isLoading: true, isInitialized: false });

          get()
            .ensureValidToken()
            .then((refreshed) => {
              if (refreshed) {
                const newToken = get().getToken();
                if (newToken) {
                  set({ isAuthenticated: true, hasToken: true, isLoading: false, isInitialized: true });
                  tokenRefreshService.startTokenMonitoring(
                    newToken,
                    (newToken) => get().setToken(newToken),
                    (error) => {
                      console.error('Automatic token refresh failed on init:', error);
                    },
                    () => get().getToken()
                  );
                } else {
                  // Refresh returned no token — now we genuinely clear
                  get().clearUser();
                }
              } else {
                // Refresh failed — keep user data but mark unauthenticated.
                // The next API call will get a 401 and the interceptor will
                // redirect to sign-in. This avoids a disruptive immediate logout
                // that could be caused by transient network issues.
                set({ isAuthenticated: false, hasToken: false, isLoading: false, isInitialized: true });
              }
            })
            .catch((error) => {
              console.error('Error refreshing token on init:', error);
              // Same non-destructive approach: keep user data, let the API
              // interceptor handle the actual logout on next 401.
              set({ isAuthenticated: false, hasToken: false, isLoading: false, isInitialized: true });
            });
          return;
        }

        // Fetch notifications if user is already authenticated
        const hydrateAndFetch = () => {
          const notificationStore = useNotificationStore.getState();
          if (notificationStore.shouldRefresh()) {
            notificationStore.fetchNotifications().catch((error) => {
              console.error('Failed to fetch notifications on init:', error);
            });
            notificationStore.fetchUnreadCount().catch((error) => {
              console.error('Failed to fetch unread count on init:', error);
            });
          }
        };
        executeWhenHydrated(useNotificationStore, hydrateAndFetch);
      },
      // Initial state
      user: null,
      isAuthenticated: false,
      hasToken: false,
      isLoading: false,
      isInitialized: false,

      // Actions
      setUser: (user: UserData) => {
        const hasToken = !!get().getToken();
        set({ user, isAuthenticated: true, hasToken, isInitialized: true });
        // Also update storage for backward compatibility
        safeStorage.setItem('userData', JSON.stringify(user));

        // Start token monitoring if we have a token
        if (hasToken) {
          const currentToken = get().getToken();
          if (currentToken) {
            tokenRefreshService.startTokenMonitoring(
              currentToken,
              (newToken) => {
                get().setToken(newToken);
              },
              (error) => {
                // Token refresh failed — don't nuke the session.
                // Let the API interceptor handle 401s gracefully.
                console.error('Automatic token refresh failed:', error);
              },
              () => get().getToken()
            );
          }
        }

        // Fetch notifications after successful login
        if (hasToken) {
          const runFetch = () => {
            const notificationStore = useNotificationStore.getState();
            notificationStore.fetchNotifications().catch((error) => {
              console.error(
                'Failed to fetch notifications after login:',
                error
              );
            });
            notificationStore.fetchUnreadCount().catch((error) => {
              console.error('Failed to fetch unread count after login:', error);
            });
          };

          // Ensure notification store is hydrated first
          executeWhenHydrated(useNotificationStore, runFetch);
        }
      },

      updateUser: (updates: Partial<UserData>) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...updates };
          set({ user: updatedUser });
          // Update storage
          safeStorage.setItem('userData', JSON.stringify(updatedUser));
        }
      },

      clearUser: () => {
        // Stop token monitoring
        tokenRefreshService.stopTokenMonitoring();

        // Disconnect WebSocket so it doesn't stay open after logout
        websocketService.disconnect();

        set({
          user: null,
          isAuthenticated: false,
          hasToken: false,
          isLoading: false,
          isInitialized: true,
        });
        // Clear storage
        safeStorage.removeItem('userData');
        get().removeToken();

        // Clear notifications when user logs out
        const notificationStore = useNotificationStore.getState();
        if (notificationStore) {
          notificationStore.clearNotifications();
        }
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      // Token management
      getToken: () => safeStorage.getItem('userToken'),
      setToken: (token: string) => {
        safeStorage.setItem('userToken', token);
        // Clear cache when token is manually set to ensure consistency
        // tokenRefreshService.clearCache(); // Temporarily disabled
      },
      removeToken: () => safeStorage.removeItem('userToken'),

      ensureValidToken: async () => {
        try {
          const currentToken = get().getToken();
          if (!currentToken) {
            return false;
          }

          // Check if token is still valid before attempting refresh
          if (tokenRefreshService.isTokenValid(currentToken)) {
            return true;
          }

          const newToken = await tokenRefreshService.manualRefresh();

          // Update token in localStorage and store
          get().setToken(newToken);

          return true;
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Don't call clearUser() here — let the caller decide.
          // This prevents aggressive logouts from transient network failures.
          return false;
        }
      },

      // Computed values
      getFullName: () => {
        const user = get().user;
        if (!user) return '';
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';
        return [firstName, lastName].filter(Boolean).join(' ') || 'User';
      },

      getLocation: () => {
        const user = get().user;
        if (!user?.current_location) return '';
        const { city, state } = user.current_location;
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return '';
      },

      getCompletion: () => {
        const user = get().user;
        if (!user) return { completed: 0, total: 5, percentage: 0 };

        let completed = 0;
        const total = 5;

        // 1. Basic Info (visa type, location, occupation, employer)
        if (
          user.visa_type &&
          user.current_location?.city &&
          user.current_location?.state
        ) {
          completed++;
        }

        // 2. Background & Identity
        if (
          user.nationality &&
          user.languages &&
          user.languages.length > 0 &&
          user.other_us_jobs &&
          user.other_us_jobs.length > 0 &&
          user.relationship_status
        ) {
          completed++;
        }

        // 3. Lifestyle & Personality
        if (
          user.hobbies &&
          user.hobbies.length > 0 &&
          user.favorite_state &&
          user.preferred_outings &&
          user.preferred_outings.length > 0 &&
          user.has_car !== undefined &&
          user.offers_rides !== undefined
        ) {
          completed++;
        }

        // 4. Travel & Exploration
        if (
          user.road_trips !== undefined &&
          user.favorite_place &&
          user.travel_tips &&
          user.willing_to_guide !== undefined
        ) {
          completed++;
        }

        // 5. Knowledge & Community
        if (
          user.mentorship_interest !== undefined &&
          user.job_boards &&
          user.job_boards.length > 0 &&
          user.visa_advice
        ) {
          completed++;
        }

        const percentage = Math.floor((completed / total) * 100);
        return { completed, total, percentage };
      },
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        hasToken: state.hasToken,
      }),
    }
  )
);
